import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

export const useFormHandler = ({
  validateForm,
  prepareFormData,
  submitForm,
  shortcuts = [],
  resetForm,
  first_ref,
  closeModal,
  setSubmitConfirm,
  submitConfirm,
  setData,
  data,
  onSuccess,
  closeOnSuccess = false,
  customFocusReset,
  isView, // Added isView
  isAlter, // Added isAlter
  onPrintAfterSave,
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const lastActiveElementRef = useRef(null);
  const savedSelectionStartRef = useRef(null);
  const savedSelectionEndRef = useRef(null);

  const { userData = null } = useSelector((state) => state.auth);
  const { voucher_type_data = [] } = useSelector((state) => state.common);

  const isYesValue = (value) => {
    if (value === true || value === 1 || value === "1") return true;
    return (
      String(value || "")
        .trim()
        .toLowerCase() === "yes"
    );
  };

  const shouldPromptVoucherPrint = useCallback(() => {
    if (!data?.voucher_type_id || !Array.isArray(voucher_type_data))
      return false;

    const matchedVoucherType = voucher_type_data.find(
      (item) => String(item?.id) === String(data?.voucher_type_id),
    );

    const printAfterSavingFlag =
      matchedVoucherType?.printing_voucher_after_saving ??
      matchedVoucherType?.print_voucher_after_saving;

    return isYesValue(printAfterSavingFlag);
  }, [data?.voucher_type_id, voucher_type_data]);

  useEffect(() => {
    if (submitConfirm) {
      const activeEl = lastActiveElementRef.current;
      if (
        activeEl &&
        (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA")
      ) {
        try {
          savedSelectionStartRef.current = activeEl.selectionStart;
          savedSelectionEndRef.current = activeEl.selectionEnd;
        } catch (e) {
          savedSelectionStartRef.current = null;
          savedSelectionEndRef.current = null;
        }
      }
    }
  }, [submitConfirm]);

  useEffect(() => {
    const handleFocusIn = (e) => {
      if (!submitConfirm) {
        const target = e.target;
        if (
          target &&
          target.tagName &&
          target !== document.body &&
          !target.closest(".modal_link")
        ) {
          lastActiveElementRef.current = target;
        }
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, [submitConfirm]);

  // Helper for consistency in exit navigation ----------------------------------------------------
  const handleNavigationBack = () => {
    if (typeof closeModal === "function") {
      closeModal();
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = async (e) => {
    if (e.type === "quit") {
      setSubmitConfirm("");
      handleNavigationBack();
    } else {
      if (isView) return; // Block submittion in view mode
      e.preventDefault();
      if (!validateForm()) {
        return;
      }
      try {
        setSubmitConfirm("");
        setLoading(true);
        const formData = prepareFormData();

        if (userData && userData.login_type === "user") {
          formData.append("user_id", userData.id || "");
          formData.append(
            "user_name",
            userData.username ||
              userData.user_name ||
              userData.ledger_name ||
              "",
          );
        }

        const response = await submitForm(formData);
        if (response?.status) {
          toast.success(response?.message || "Operation successful!", {
            position: toast.POSITION.TOP_CENTER,
          });
          if (location.pathname === "/signUp") {
            setTimeout(() => {
              navigate("/");
              setLoading(false);
            }, 100);
          }

          // Explicit secondary-modal behavior: close immediately after successful save.
          if (closeOnSuccess && closeModal) {
            closeModal();
            return;
          }

          const isChartOfAccountModal =
            location.pathname === "/chart-of-account-accounting-master-group" ||
            location.pathname ===
              "/chart-of-account-accounting-master-ledger" ||
            location?.pathname === "/chart-of-account-invmas-stockgroup" ||
            location?.pathname === "/chart-of-account-invmas-stockcategory" ||
            location?.pathname === "/chart-of-account-invmas-stockcompany" ||
            location?.pathname === "/chart-of-account-invmas-godown" ||
            location?.pathname === "/chart-of-account-invmas-unit" ||
            location?.pathname ===
              "/chart-of-account-accounting-master-voucherstype" ||
            location?.pathname === "/chart-of-account-invmas-stockitem" ||
            location?.pathname ===
              "/chart-of-account-accounting-master-currencies" ||
            location?.pathname ===
              "/chart-of-account-accounting-master-budgets" ||
            location?.pathname ===
              "/chart-of-account-accounting-master-scenarios";

          if (isChartOfAccountModal && closeModal) {
            closeModal();
            return;
          }

          if (onSuccess) {
            onSuccess(response);
          }

          const canPromptPrint =
            typeof onPrintAfterSave === "function" &&
            !isView &&
            shouldPromptVoucherPrint();

          if (canPromptPrint) {
            const userConfirmedPrint = window.confirm(
              "Print voucher after saving?",
            );

            if (userConfirmedPrint) {
              onPrintAfterSave(response);
              handleFocusReset();
              return;
            }
          }

          // Handle navigation after Alter success ----------------------------------------
          if (isAlter) {
            handleNavigationBack();
            return;
          }

          if (resetForm) {
            resetForm();
          }
          handleFocusReset();
        } else {
          setLoading(false);
          if (response?.serial_no) {
            setData({ ...data, serial_no: response?.serial_no });
          }
          handleFocusReset();
          toast.warn(response?.message || "Operation failed!", {
            position: toast.POSITION.TOP_CENTER,
          });
        }
      } catch (error) {
        setLoading(false);
        console.error(error);
        handleFocusReset();
        toast.error(error?.message || "An error occurred!", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }
  };
  const handleFocusReset = useCallback(
    (response) => {
      setSubmitConfirm("");
      setLoading(false);

      // Use last active element if still in DOM, otherwise fall back
      if (
        lastActiveElementRef.current &&
        document.body.contains(lastActiveElementRef.current)
      ) {
        const activeEl = lastActiveElementRef.current;
        window.isRestoringVoucherFocus = true;
        activeEl.focus();
        if (
          savedSelectionStartRef.current !== null &&
          savedSelectionEndRef.current !== null &&
          (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA")
        ) {
          try {
            activeEl.setSelectionRange(
              savedSelectionStartRef.current,
              savedSelectionEndRef.current,
            );
          } catch (e) {
            console.warn(e);
          }
        }
        window.isRestoringVoucherFocus = false;
      } else if (customFocusReset) {
        customFocusReset();
      } else if (first_ref?.current) {
        first_ref.current.focus();
      }
    },
    [setSubmitConfirm, setLoading, customFocusReset, first_ref],
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      shortcuts.forEach((shortcut) => {
        if (!e || !e.key || !shortcut || !shortcut.key) return;
        const isMatch =
          e.key.toLowerCase() === shortcut.key.toLowerCase() &&
          e.ctrlKey === !!shortcut.ctrlKey &&
          e.altKey === !!shortcut.altKey;

        if (isMatch) {
          e.preventDefault();
          // Block specific shortcut actions in view mode
          if (isView) {
            // Allow navigation shortcuts like Esc or Q
            if (shortcut.key.toLowerCase() === "q" || e.key === "Escape") {
              shortcut.action();
            }
            return;
          }
          shortcut.action();
        }
      });

      if (submitConfirm === "Accept") {
        if (isView) return; // Block accept logic in view mode
        if (e.key.toLowerCase() === "y") {
          e.preventDefault();
          handleSubmit(new Event("submit"));
        } else if (e.key.toLowerCase() === "n" || e.key === "Escape") {
          e.preventDefault();
          handleFocusReset();
        }
      } else if (submitConfirm === "Quit") {
        if (e.key.toLowerCase() === "y") {
          e.preventDefault();
          handleSubmit(new Event("quit"));
        } else if (e.key.toLowerCase() === "n" || e.key === "Escape") {
          e.preventDefault();
          handleFocusReset();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [submitConfirm, shortcuts]);

  return { handleSubmit, loading, handleFocusReset };
};

export const prepareFormData = (fields) => {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // Process array items to clean common amount fields
      const cleanedArray = value
        .filter((item) => {
          // Filter out rows that are 'End of List', 'Not Applicable' or simply empty
          const isInvalid = (val) =>
            val === "End of List" ||
            val === "Not Applicable" ||
            val === "" ||
            val === null ||
            val === undefined;

          if (item.hasOwnProperty("item_name") && isInvalid(item.item_name))
            return false;
          if (
            item.hasOwnProperty("ledger_account") &&
            isInvalid(item.ledger_account)
          )
            return false;
          if (
            item.hasOwnProperty("payment_ledger_account") &&
            isInvalid(item.payment_ledger_account)
          )
            return false;
          if (
            item.hasOwnProperty("particular_ledger_name") &&
            isInvalid(item.particular_ledger_name)
          )
            return false;
          if (item.hasOwnProperty("first") && isInvalid(item.first))
            return false;

          return true;
        })
        .map((item) => {
          const newItem = { ...item };
          const amountFields = [
            "particular_ledger_pay_amount",
            "payment_ledger_amount",
            "item_amt",
            "item_rate",
            "ledger_amount",
            "amount",
            "total_amount",
            "qty",
            "rate",
            "item_qty",
          ];
          amountFields.forEach((amtField) => {
            if (newItem[amtField] !== undefined && newItem[amtField] !== null) {
              newItem[amtField] = String(newItem[amtField]).replace(/,/g, "");
            }
          });
          return newItem;
        });
      formData.append(key, JSON.stringify(cleanedArray));
    } else if (value !== null && value !== undefined) {
      let cleanedValue = String(value);
      // Clean top-level amount fields
      if (
        [
          "total_amount",
          "voucher_amount",
          "amount",
          "item_amt",
          "ledger_amount",
        ].includes(key)
      ) {
        cleanedValue = cleanedValue.replace(/,/g, "");
      }
      formData.append(key, cleanedValue);
    }
  });
  return formData;
};
